<script setup lang="ts">
interface ButtonSpec {
  title: string
  sound?: string
  disabled?: boolean
  // #898: Dialog.vue と同じ形にする（イベントダイアログが器を差し替えられるように）。
  effect?: string
  color?: string
}

interface Props {
  title?: string
  // backButton?: ButtonSpec
  submitButton?: ButtonSpec
  middleButton?: ButtonSpec
  cancelButton?: ButtonSpec
  /**
   * submit で自動的に閉じない (#787)。Dialog.vue と同型。
   *
   * 既定では emit 直後に閉じるが、emit は親の非同期ハンドラを **await しない**ので、
   * 時間の掛かる送信では「押した瞬間に消えて、完了まで無表示」になる。これを渡すと
   * 閉じる判断が親に移り、親は成功したときだけ v-model を倒せばよくなる
   * （＝失敗時は入力を保ったまま留まれる）。
   *
   * **この prop が無い呼び出し元の挙動は一切変わらない。**
   */
  keepOpenOnSubmit?: boolean
  /** 送信中。ボタンを loading + 操作不能にする（#787）。 */
  busy?: boolean
  /**
   * 本文（既定スロット）だけ、単位 `--u` を何倍にするか（#957）。情報の多い画面（スキル）で字を少し小さくする。
   * 器（大きさ・題・ボタン）はほかのダイアログと同じまま。本文の中の `calc(N * var(--u))` はすべて連動する。
   */
  contentUnitScale?: number
}

const dialog = defineModel<boolean>()
const props = defineProps<Props>()
const emit = defineEmits(['submit', 'cancel', 'middle'])

// #721: モバイルでは 4:3 固定だと Stripe フォーム（配送先・カード入力）が窮屈で
// 崩れるため、中央寄せの浮遊モーダルにする（Dialog.vue と同型。:fullscreen は
// Vuetify の fullscreen !important 規則がカスケード順で不安定になるため使わない）。
const { isMobile } = useIsMobile()

// const submitSound = ref<HTMLAudioElement | null>(null)
// const cancelSound = ref<HTMLAudioElement | null>(null)
const loading = ref<boolean>(false)

const { $sound } = useNuxtApp()

const onSubmitAction = async (
  event: 'submit' | 'cancel' | 'middle',
  spec: ButtonSpec | undefined,
) => {
  try {
    loading.value = true

    if (spec) {
      const soundType = spec.sound || event
      await $sound.playSound(soundType, { wait: true })

      if (spec.disabled) {
        return
      }
    }

    emit(event)
  }
  finally {
    loading.value = false
    // #787: keepOpenOnSubmit のときは submit で閉じない。閉じる判断は親が持つ。
    if (!(event === 'submit' && props.keepOpenOnSubmit)) {
      dialog.value = false
    }
  }
}
</script>

<template>
  <v-dialog
    id="fantasy-dialog"
    v-model="dialog"
    persistent
    scrim
    :class="{ 'fantasy-dialog--mobile': isMobile }"
  >
    <v-sheet class="fantasy-dialog-sheet">
      <v-card variant="text">
        <v-card-title
          v-if="title"
          class="fantasy-dialog-title"
        >
          {{ title }}
        </v-card-title>

        <!-- スクロールしない補助スロット (説明・警告など本文の上に常に出したいもの) -->
        <div
          v-if="$slots.hint"
          class="fantasy-dialog-hint"
        >
          <slot name="hint" />
        </div>

        <v-card-text class="fantasy-dialog-text">
          <!-- 本文だけ単位を変える（器の高さ・余白は v-card-text の --u のまま。#957） -->
          <div
            v-if="contentUnitScale && contentUnitScale !== 1"
            class="fantasy-dialog-content"
            :style="{ '--u': `calc(var(--u-base) * ${contentUnitScale})` }"
          >
            <slot> 読み込み中 </slot>
          </div>
          <slot v-else>
            読み込み中
          </slot>
        </v-card-text>

        <!-- #904: middle があるときだけ、middle を**器の中央に浮かせる**。flex のまま
             spacer で寄せると、**submit が無いときに middle が右へ寄る**（spacer が
             2 本とも生きて「cancel の右端と器の右端の中点」に来る）。
             ⚠️ グリッド（1fr/auto/1fr）で解くと、**ボタンの `min-width: 25%` が
             「列」に対して効いて「とじる」が激しく縮む** —— % は containing block
             （＝グリッド領域）に対して解決されるため。浮かせるなら flow は 2 ボタンの
             ときと同じままなので、既存のサイズが一切動かない。 -->
        <v-card-actions
          class="fantasy-dialog-actions"
          :class="{ 'fantasy-dialog-actions--three': middleButton }"
        >
          <v-btn
            v-if="cancelButton"
            variant="outlined"
            class="fantasy-dialog-cancel-button"
            :text="cancelButton?.title"
            :disabled="cancelButton?.disabled || busy"
            :loading="loading"
            @click="onSubmitAction('cancel', cancelButton)"
          />

          <!-- #898: Props には元からあったが描画されていなかった。Dialog.vue と同型。 -->
          <v-btn
            v-if="middleButton"
            variant="outlined"
            class="fantasy-dialog-submit-button fantasy-dialog-middle-button"
            :text="middleButton?.title"
            :disabled="middleButton?.disabled || busy"
            :loading="loading"
            @click="onSubmitAction('middle', middleButton)"
          />

          <v-spacer />

          <!-- #898: `effect: 'glow'` はこの器では効かない。glow の CSS は Dialog.vue の
               scoped style にあり、ここへ複製すると同じ CSS の 5 本目のコピーになる
               （GiftKeyCard / SignUpNow / ProductCard に色違いが既にある）。
               `effect` を使うイベントは現時点で 1 つも無いので、器を 1 本化するとき
               （Dialog / LargeDialog の統合）にまとめて解消する。 -->
          <v-btn
            v-if="submitButton"
            variant="outlined"
            class="fantasy-dialog-submit-button"
            :text="submitButton?.title"
            :color="submitButton?.color"
            :disabled="submitButton?.disabled || busy"
            :loading="loading || busy"
            @click="onSubmitAction('submit', submitButton)"
          />
        </v-card-actions>
      </v-card>
    </v-sheet>
    <!--
    <audio
      ref="cancelSound"
      preload="auto"
    >
      <source
        src="@/audio/level-up-3-199576.mp3"
        type="audio/mpeg"
      >
    </audio>

    <audio
      ref="submitSound"
      preload="auto"
    >
      <source
        src="@/audio/decidemp3-14575.mp3"
        type="audio/mpeg"
      >
    </audio> -->
  </v-dialog>
</template>

<style scoped>
/* 本文だけ単位を変えるときの入れ物。本文の字の大きさも新しい --u で決め直す */
.fantasy-dialog-content {
  height: 100%;
  font-size: max(11px, calc(1.875 * var(--u)));
}

#fantasy-dialog {
  width: 100%;
  aspect-ratio: 4 / 3;
  max-width: 133.333vh;
  max-height: 100vh;
}

.fantasy-dialog-sheet {
  background-color: #f5d38c;
  background-image: url("@/assets/images/556492_800_m.webp");
  background-size: 100% 100%;

  font-family: "Utsukushi" !important;
  font-size: max(13.33px, calc(1.875 * var(--u))) !important;

  width: 100%;
  height: 100%;

  padding: max(13.33px, calc(1.875 * var(--u))) max(17.78px, calc(2.5 * var(--u)));
}

#fantasy-dialog .v-card-title {
  padding: max(8.89px, calc(1.25 * var(--u)));
  padding-top: 0;

  font-size: max(20px, calc(2.81 * var(--u)));
  font-weight: bold;
  text-align: center;
}

/* .fantasy-dialog-title {
  font-size: 150%;
  font-weight: bold;
  text-align: center;
} */

.fantasy-dialog-title::before {
  content: "\2741";
  margin-right: 8px;
  color: brown;
}

.fantasy-dialog-title::after {
  content: "\2741";
  margin-left: 8px;
  color: brown;
}

#fantasy-dialog .v-card-text {
  /* padding: clamp(8.89px, 1.25dvw, 16px); */
  /* padding-top: 0; */
  padding: 0;
}

/* 本文 (スクロール領域) の上に固定で表示される補助領域。
   font-size は本文と同じスケールに揃える。 */
.fantasy-dialog-hint {
  font-size: max(13.33px, calc(1.875 * var(--u)));
  font-weight: normal;
  color: #5c2c0c;
  text-align: center;
  line-height: 1.4;
  padding: 0 max(4px, calc(0.5 * var(--u)));
  margin-top: max(4px, calc(0.5 * var(--u)));
}

.fantasy-dialog-text {
  /* font-size: 1.5rem; */
  font-size: max(13.33px, calc(1.875 * var(--u))) !important;
  font-weight: bold;

  /* height: clamp(193.33px, 27.19dvw, 348px); */
    /* height: clamp(282px, 39.69dvw, 508px); */
    /* height: clamp(337.78px, 47.5dvw, 608px); */
    /* height: clamp(383.70px, 53.96dvw, 1036px); */
    /* height: clamp(396.44px, 61.94dvw, 892px); */
  /* height: clamp(318.22px, 49.72vw, 716px); */
  /* height: clamp(405.33px, 63.33vw, 912px); */
  height: max(304px, calc(47.5 * var(--u)));

  margin: 16px 0;

  /* 子要素の position: absolute 用（#654 rating overlay 等。Dialog.vue と同型 / #898）。 */
  position: relative;

  overflow-y: auto;
  scrollbar-color: #B2EBF2C0 transparent;
  scrollbar-width: thin;
}

.fantasy-dialog-text :deep(strong) {
  color: crimson;
}

.fantasy-dialog-text :deep(em) {
  color: blue;
  font-style: normal;
}

.fantasy-dialog-text :deep(li) {
  margin-left: 1em;
}

.fantasy-dialog-text :deep(.note) {
  font-size: 1.0rem;
  color: darkslategrey;
}

.fantasy-dialog-text :deep(pre) {
  overflow-x: auto;
  max-width: 100%;
  font-size: max(10px, calc(1.2 * var(--u)));
  line-height: 1.4;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  padding: max(4px, calc(0.5 * var(--u)));
  margin: max(4px, calc(0.5 * var(--u))) 0;
}

.fantasy-dialog-text :deep(pre code) {
  font-family: "Courier New", monospace;
  font-size: max(10px, calc(1.2 * var(--u)));
}

#fantasy-dialog .v-card-actions {
  padding: max(4.44px, calc(0.625 * var(--u)));
}

.fantasy-dialog-actions .v-btn {
  /* font-size: 1.5rem; */
  font-size: max(13.33px, calc(1.875 * var(--u)));
  font-weight: bold;

  /* padding: 1rem; */
  padding: max(8.89px, calc(1.25 * var(--u)));
  min-width: 25%;
  height: auto;
}

/* #898: 名前は Dialog.vue の `fantasy-dialog-back-button` と揃えたいが、E2E
   （crystallize-visual.spec.ts）と単体テストがこの名前を掴んでいるので据え置く。
   器を 1 本化するときに一緒に直す。 */
.fantasy-dialog-actions .fantasy-dialog-cancel-button {
  color: #404040;
  background-color: #80808040;
}

.fantasy-dialog-actions .fantasy-dialog-submit-button {
  color: #404040;
  background-color: #f0f0f040;
}

/* #904: middle を **器そのものの中央**に浮かせる。flow から外すので、cancel と submit の
   並びとサイズは 2 ボタンのときとまったく同じまま（`min-width: 25%` も器に対して効く）。 */
.fantasy-dialog-actions--three {
  position: relative;
}

.fantasy-dialog-actions--three .fantasy-dialog-middle-button {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

/* モバイルは器が狭く、浮かせると両脇のボタンと重なりうる。流れに戻す。 */
@media (max-width: 768px) {
  .fantasy-dialog-actions--three .fantasy-dialog-middle-button {
    position: static;
    transform: none;
  }
}

/* #721: モバイルは中央寄せの浮遊モーダル。4:3 前提の固定高さ・小さい文字をやめ、
   タイトル（上）／本文=Stripe フォーム（中・縦スクロール）／ボタン（下・固定）に。
   :fullscreen をやめ、Dialog.vue と同型の「id 付き高 specificity ＋ !important で
   シートサイズを勝たせる」方式にする（dev の per-component CSS 注入順ブレでカスケードが
   不安定になり、常設 EventDialog と v-if の detail で浮遊の効きがブレた #721 調査より）。 */
@media (max-width: 768px) {
  #fantasy-dialog.fantasy-dialog--mobile {
    aspect-ratio: auto;
    max-width: none;
    max-height: none;
  }

  /* #721 2巡目: content box を全幅にして Vuetify 既定の margin:24px+max-width 制約を外し、
     シートを margin:0 auto で左右対称に中央化する（Dialog.vue と同型）。 */
  #fantasy-dialog.fantasy-dialog--mobile :deep(.v-overlay__content) {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
  }

  #fantasy-dialog.fantasy-dialog--mobile .fantasy-dialog-sheet {
    /* #721 2巡目: 羊皮紙の外側の透過余白を左右対称に（狭い側 ≈7px に統一）。Dialog.vue と同型。
       Vuetify の content box(max-width:calc(100%-48px)+margin:24px)を右へはみ出して非対称に
       なるので、シート幅を広げて margin:0 auto で over-wide ブロックを中央化する。 */
    width: calc(100dvw - 14px) !important;
    max-width: 560px !important;
    height: auto !important;
    max-height: 86dvh !important;
    min-height: 0;
    margin: 0 auto !important;

    background-size: cover;
    background-position: center;
    /* #721: 角丸を -30%（18→13px）。Dialog.vue と揃える。 */
    border-radius: 13px !important;
    overflow: hidden;
    box-shadow: 0 12px 34px rgba(0, 0, 0, 0.55);

    display: flex;
    flex-direction: column;

    padding: 16px 16px 14px;
  }

  .fantasy-dialog--mobile .fantasy-dialog-sheet > .v-card {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
  }

  .fantasy-dialog--mobile #fantasy-dialog .v-card-title,
  .fantasy-dialog--mobile .fantasy-dialog-title {
    flex: 0 0 auto;
    font-size: 1.4rem !important;
  }

  .fantasy-dialog--mobile .fantasy-dialog-text {
    flex: 1 1 auto;
    height: auto;
    min-height: 0;
    max-height: none;
    overflow-y: auto;
    font-size: 1rem !important;
  }

  /* #721 3巡目: 本文とボタンの間を「ボタンエリア下の余白」と同じ ≈1行分空ける。
     id 指定の #fantasy-dialog .v-card-actions { padding } に負けないよう id を前置する
     （Dialog.vue と同型。実測で class だけでは padding-top が効かなかった）。 */
  #fantasy-dialog.fantasy-dialog--mobile .fantasy-dialog-actions {
    flex: 0 0 auto;
    flex-wrap: wrap;
    gap: 10px;
    padding-top: 18px;
  }

  .fantasy-dialog--mobile .fantasy-dialog-actions .v-btn {
    font-size: 1rem;
    /* #721 2巡目: ボタンは伸ばさず一定幅（min-width 40%）で揃える。flex-grow を切ることで、
       ボタンが 1 つだけ（Stripe 購入ダイアログの「とじる」）でも横に間延びしない。 */
    flex: 0 1 auto;
    min-width: 40%;
  }
}
</style>
